import { cn } from "@/lib/utils";
import { JSX, useEffect, useState } from "react";
import { Link } from "react-router";

import {
  InfoIcon,
  closingCross as closingCrossIcon,
  error as errorIcon,
  success as successIcon,
  warning as warningIcon,
} from "../../../../designSystemIcons";
import { Alert as AlertType } from "../../../../redux/message/messageSlice";

type Props = {
  alert: AlertType;
  onHandleClose: () => void;
  stackIndex?: number;
};

export const Alert = ({ alert, onHandleClose, stackIndex = 0 }: Props) => {
  const { title, description, type, persistent, link } = alert;
  const [fadeIn, setFadeIn] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  // Stack styling: newer alerts on top, older ones slightly offset behind
  const stackOffset = Math.min(stackIndex, 3) * 8; // Max 3 visible layers, 8px offset each
  const stackScale = 1 - Math.min(stackIndex, 3) * 0.03; // Slightly smaller for background alerts
  const stackOpacity =
    stackIndex === 0 ? 1 : Math.max(0.7, 1 - stackIndex * 0.15);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let automaticCloseTimeoutId: NodeJS.Timeout;
    if (fadeOut) {
      setFadeIn(false);
      timeoutId = setTimeout(() => onHandleClose(), 400);
    } else {
      setFadeIn(true);
    }
    if (!persistent) {
      automaticCloseTimeoutId = setTimeout(() => setFadeOut(true), 4600);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(automaticCloseTimeoutId);
    };
  }, [fadeOut]);

  const onClose = () => {
    setFadeOut(true);
  };

  let icon: React.JSX.Element;

  switch (type) {
    case "success":
      icon = successIcon;
      break;
    case "error":
      icon = errorIcon;
      break;
    case "warning":
      icon = warningIcon;
      break;
    case "info":
      icon = <InfoIcon innerFill="transparent" />;
      break;
    default:
      icon = <InfoIcon innerFill="transparent" />;
  }

  return (
    <article
      className={cn(
        "flex flex-row py-2 pl-2 text-white rounded-xl w-full desktop:w-[444px] shadow-alert backdrop-blur-xs transition-all duration-400",
        type === "success" && "bg-secondary-green/90",
        type === "error" && "bg-primary-red/90",
        type === "warning" && "bg-primary-yellow/90",
        type === "info" && "bg-primary-blue/90",
        fadeIn ? "opacity-100" : "opacity-0",
        stackIndex === 0 ? "relative" : "absolute top-0 left-0 right-0",
      )}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      style={{
        transform: `translateY(${stackOffset}px) scale(${stackScale})`,
        opacity: fadeIn ? stackOpacity : 0,
        zIndex: 50 - stackIndex,
      }}
    >
      {/* <span className="shrink-0 size-3 mr-2">{icon}</span> */}
      <div className="flex flex-col w-full ml-2">
        <p className="text-xl font-semibold">{title}</p>
        <p className="my-auto">{description}</p>
        {link && (
          <Link
            to={link.to}
            onClick={onHandleClose}
            className="text-sm font-semibold underline mt-1"
          >
            {link.label}
          </Link>
        )}
      </div>
      <button className="fill-white p-3" onClick={onClose}>
        {closingCrossIcon}
      </button>
    </article>
  );
};
